import React, {useEffect, useState} from "react";
import { Btn, OrderFinalSummary, PaymentItemsColumn } from "../components";
import { useCore, useEnsureCheckoutOpen } from "../hooks";
import { useNavigate } from "react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useGQL } from "../utils/GQLContext.ts";
import {
  formatDate,
  getDateStringFromUTCString, OrderItemType,
  PaymentTerminal,
  prepareOrderItems,
  preparePaymentItems, round2, Schema,
  wait
} from "../utils";
import { CustomCheckoutOrder, initOrder } from "../store/core/reducer.ts";
import {OrderItem, PaymentItem} from "depoto-core";
import {printDocument, printTicket} from "../utils/print.ts";

export const PreviewBill: React.FC = () => {
  const [isFetching, setIsFetching] = useState<boolean>(false)
  const gql = useGQL()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { core, currentOrder, setCurrentOrder, selectedCheckoutId, setOrderVouchers } = useCore()
  const ensureCheckoutOpen = useEnsureCheckoutOpen()
  const [isUsingComgateTerminal, setIsUsingComgateTerminal] = useState<boolean>(JSON.parse(window.localStorage.getItem('depoto_checkout2_comgate_allowed')))
  const [comgateResult, setComgateResult] = useState<'IN_PROGRESS' | 'OK' | 'FAILED' | 'EMV_USER_CANCEL' | 'NONE'>('NONE')
  const [cardPaymentItem, setCardPaymentItem] = useState<PaymentItem>()

  useEffect(() => {
    if (currentOrder?.paymentItems?.filter(pi => pi?.payment?.type?.id === 'card').length) {
      setCardPaymentItem(currentOrder?.paymentItems?.find(pi => pi.payment.type.id === 'card'))
    } else {
      setCardPaymentItem(undefined)
    }
  }, [currentOrder])

  const { mutate: createOrder } = useMutation({
    mutationKey: ['createOrder'],
    mutationFn: (order: CustomCheckoutOrder) =>
      toast.promise(
        gql('mutation')({
          createOrder: [
            {
              checkout: Number(selectedCheckoutId),
              status: 'bill',
              note: order.note,
              privateNote: order.privateNote,
              dateCreated: getDateStringFromUTCString(new Date().toUTCString()),
              items: prepareOrderItems(order),
              paymentItems: preparePaymentItems(order),
              customer: order.customer?.id > 0 ? order.customer.id : null,
              rounding: order.rounding || 0,
            },
            { data: { id: true }, errors: true },
          ],
        }).then(res => {
          if (!res.createOrder.data || res.createOrder.errors?.length > 0) {
            throw new Error(res.createOrder.errors?.join('\n') || 'Chyba při vytváření objednávky')
          }
          return res
        }),
        {
          pending: 'Vytvářím objednávku...',
          error: 'Něco se pokazilo',
          success: 'Objednávka vytvořena',
        },
      ),
    onSuccess: async (res) => {
      const createdOrder = await core.services.order.getById(res.createOrder.data.id, Schema.order.detail)
      await printDocument(createdOrder.invoiceReceiptUrl, false, true, core)
      await wait(200)
      setIsFetching(false)
      queryClient.invalidateQueries({ queryKey: ['products', 'orders'] })
      setCurrentOrder(initOrder())
      setOrderVouchers([])
      setComgateResult('NONE')
      backToDashboard()
    },
    onError: (error: Error) => {
      setIsFetching(false)
      error.message?.split('\n').forEach(msg => toast.error(msg))
    }
  })

  const { mutate: createOrderFromReservation } = useMutation({
    mutationKey: ['updateOrder'],
    mutationFn: (order: CustomCheckoutOrder) =>
      toast.promise(
        gql('mutation')({
          updateOrder: [
            {
              id: order.id,
              checkout: Number(selectedCheckoutId),
              status: 'bill',
              note: order.note,
              privateNote: order.privateNote,
              dateCreated: getDateStringFromUTCString(new Date().toUTCString()),
              items: prepareOrderItems(order),
              paymentItems: preparePaymentItems(order),
              customer: order.customer?.id > 0 ? order.customer.id : null,
              rounding: order.rounding || 0,
            },
            { data: { id: true }, errors: true },
          ],
        }).then(res => {
          if (!res.updateOrder.data || res.updateOrder.errors?.length > 0) {
            throw new Error(res.updateOrder.errors?.join('\n') || 'Chyba při vytváření objednávky z rezervace')
          }
          return res
        }),
        {
          pending: 'Vytvářím objednávku z rezervace...',
          error: 'Něco se pokazilo',
          success: 'Objednávka vytvořena',
        },
      ),
    onSuccess: async (res) => {
      const createdOrder = await core.services.order.getById(res.updateOrder.data.id, Schema.order.detail)
      await printDocument(createdOrder.invoiceReceiptUrl, false, true, core)
      await wait(200)
      setIsFetching(false)
      queryClient.invalidateQueries({ queryKey: ['products', 'orders'] })
      setCurrentOrder(initOrder())
      setOrderVouchers([])
      setComgateResult('NONE')
      backToDashboard()
    },
    onError: (error: Error) => {
      setIsFetching(false)
      error.message?.split('\n').forEach(msg => toast.error(msg))
    }
  })

  const confirmOrder = async () => {
    const paymentsSum = round2(currentOrder?.paymentItems?.map(p => Number(p.amount)).reduce((a, b) => Number(a) + Number(b), 0))
    if (paymentsSum < currentOrder.priceAll) {
      toast.error('Částka nesouhlasí - platba není dostatečná')
      return
    }
    const isPaymentACoupon = currentOrder?.paymentItems?.find(p => p.payment.type.id === 'coupon')
    // todo: now is allowed to pay with coupon, if order price is less than coupon value. fix later: set payment item coupon value to exact value diff (or order price if not combination)
    if (paymentsSum > currentOrder.priceAll && !isPaymentACoupon) {
      toast.error('Částka nesouhlasí - platba je vyšší než cena objednávky')
      return
    }
    if (currentOrder?.items?.length === 0) {
      toast.error('Objednávka neobsahuje položky')
      return
    }
    if (currentOrder?.items?.find(oi => oi.type === OrderItemType.voucher && !(oi.price < 0))) {
      toast.error('Uplatněný poukaz má nulovou hodnotu (chybí zboží se stejnou sazbou DPH), odeberte ho z objednávky')
      return
    }
    // if (currentOrder?.paymentItems?.filter(pi => !pi.amount)?.length) {
    //   toast.error('Částka platby nesmí být nulová')
    //   return
    // }
    console.log(currentOrder)
    setIsFetching(true)
    if (!(await ensureCheckoutOpen())) {
      setIsFetching(false)
      return
    }
    let order: CustomCheckoutOrder
    try {
      order = await createVouchers()
    } catch (e) {
      setIsFetching(false)
      toast.error(`Chyba při vytváření poukazu: ${e instanceof Error ? e.message : e}`)
      return
    }
    await wait(100)
    if (currentOrder?.id > 0) {
      // todo: this updateReservation is hack, remove on server fix (newly added items are not in moves, when bill is created from reservation, without updating reservation before..)
      // redeemed voucher is sent only in updateOrder (core does not know voucher fields)
      const updatedReservation = await core.services.order.updateOrderPart({ id: order.id, items: order.items.filter(oi => oi.type !== OrderItemType.voucher) })
      console.log({updatedReservation})
      await wait(100)
      createOrderFromReservation(order)
    } else {
      createOrder(order)
    }
  }

  // creates vouchers for sold voucher items and links them to order items (sent as orderItem.voucher)
  const createVouchers = async (): Promise<CustomCheckoutOrder> => {
    const items = [...currentOrder.items]
    try {
      for (const [i, oi] of items.entries()) {
        if (oi.type !== OrderItemType.voucherToCreate || oi.voucherId > 0) {
          continue
        }
        const res = await gql('mutation')({
          createVoucher: [
            {
              name: oi.name,
              code: oi.code,
              discountType: 'value',
              discountValue: oi.price,
              maxUse: 1,
              validFrom: formatDate(new Date(), true),
              validTo: formatDate(new Date(oi.expirationDate), true),
              enabled: true,
              isPayment: oi.isForSubsequentSettlement,
            },
            { data: { id: true }, errors: true },
          ],
        })
        if (!res.createVoucher.data?.id || res.createVoucher.errors?.length > 0) {
          throw new Error(res.createVoucher.errors?.join('\n') || oi.code)
        }
        items[i] = { ...oi, voucherId: res.createVoucher.data.id }
      }
    } finally {
      // keep created voucher ids in store, so retry (after failed voucher or order) does not create them again
      setCurrentOrder({ ...currentOrder, items: [...items] })
    }
    return { ...currentOrder, items }
  }

  const backToDashboard = () => {
    navigate('/dashboard')
  }

  // todo terminal in settings, enable card payments:
  const makePayment = async () => {
    const pTerm = new PaymentTerminal('192.168.0.207', 33350, true)
    setTimeout(() => pTerm.cancelCurrentOperation(), 10000) // todo cancel in progress test
    const payment = await pTerm.startPayment({
      amountFloat: 1,
      currencyId: 'CZK',
      invoiceNumber: '1234',
    })
    console.log({ payment })
  }

  const startComgateTerminalPayment = async () => {
    const ip = window.localStorage.getItem('depoto_checkout2_comgate_ip')
    const port = window.localStorage.getItem('depoto_checkout2_comgate_port')
    setComgateResult('IN_PROGRESS')
    if (!(await ensureCheckoutOpen())) {
      setComgateResult('NONE')
      return
    }
    const pTerm = new PaymentTerminal(ip, Number(port), false)
    // setTimeout(() => pTerm.cancelCurrentOperation(), 10000) // todo cancel in progress test
    const payment: any = await pTerm.startPayment({
      amountFloat: cardPaymentItem?.amount,
      currencyId: 'CZK',
      invoiceNumber: `${currentOrder?.reservationNumber}`,
    })
    console.log('res: ', payment.operationResult.responseCode)
    if (payment.operationResult.responseCode === 'OK') {
      setComgateResult('OK')
    } else if (payment.operationResult.responseCode === 'EMV_USER_CANCEL') {
      setComgateResult('EMV_USER_CANCEL')
    } else {
      setComgateResult('FAILED')
    }
    // console.log({ payment })
    // todo payment.operationResult.responseCode === 'OK' ? hide btn!
  }

  return currentOrder?.items?.length > 0 ? (
    (
      <div className="w-full h-body flex flex-col overflow-y-auto">
        <div className="flex flex-row">
          <div className="w-1/2   p-4">
            <OrderFinalSummary order={currentOrder} isReservation={false} />
          </div>
          <div className="w-1/2">
            <PaymentItemsColumn />
          </div>
        </div>
        <div className="flex flex-row bg-white p-4 mt-auto justify-end">
          {isUsingComgateTerminal && cardPaymentItem ? (
            <Btn
              className="btn-info btn-xl uppercase min-w-88"
              children={'Zaplatit na Comgate terminálu'}
              icon={'fa-credit-card'}
              isDisabled={comgateResult === 'OK' || comgateResult === 'IN_PROGRESS'}
              onClick={startComgateTerminalPayment} />
          ) : null}
          <div>
            {
              comgateResult === 'IN_PROGRESS' ? 'Platba probíhá'
                : comgateResult === 'OK' ? 'Platba proběhla v pořádku'
                  : comgateResult === 'EMV_USER_CANCEL' ? 'Platba stornována'
                    : comgateResult === 'FAILED' ? 'Chyba při platbě'
                      : null}
          </div>
          <Btn className="btn-success btn-xl uppercase min-w-88" children={'Vystavit doklad'} icon={'fa-receipt'} isDisabled={isFetching} onClick={confirmOrder} />
        </div>
      </div>
    )
  ) : <div>Vložte položky do objednávky</div>
}
